import { ApolloError, useApolloClient } from "@apollo/client";
import { Alert } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Formik, FormikProps } from "formik";
import React, { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApolloError as ApolloErrorView } from "src/components/Feedback/ApolloError";
import { GenericError } from "src/components/Feedback/GenericError";
import { OrganizationError as OrganizationErrorView } from "src/components/Feedback/OrganizationError";
import { School } from "src/components/Inputs/MultiSelectSchoolRank";
import { Person } from "src/components/Layout/Avatar";
import { FormLayout } from "src/components/Layout/FormLayout";
import {
  FormProvider,
  FormWithAnswersTracker,
  useFormContext,
  useVisibilityContext,
  VisibilityContext,
} from "src/components/Providers/FormProvider";
import { useGlossary } from "src/hooks/useGlossary";
import {
  isOrganizationError,
  useOrganization,
} from "src/hooks/useOrganization";
import { usePreviousFormSchools } from "src/hooks/usePreviousFormSchools";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import useRankedSchools from "src/hooks/useSchoolRank";
import { useWeglotToast } from "src/plugins/weglot";
import { refreshFormAdmissionsAfterMutation } from "src/scenes/parent/formTemplates/admissions";
import {
  GET_PARENT_ASSIGNED_FORMS,
  GET_PARENT_FORMS_INDEX,
} from "src/scenes/parent/formTemplates/graphql/queries";
import { LATE_EDIT_STATUSES } from "src/scenes/parent/forms/formEditability";
import * as FormTemplate from "src/services/formTemplate";
import {
  isFreeTextAnswer,
  isGradesAnswer,
  isMultiSelectAnswer,
  isSingleSelectAnswer,
} from "src/services/formTemplate/answer";
import { getCompleteApplicableQuestions } from "src/services/formTemplate/question";
import {
  findGeneralSections,
  findSchoolRankingSection,
} from "src/services/formTemplate/section";
import * as AFF from "src/services/formTemplateFilters";
import { validateFormTemplate } from "src/services/formTemplateValidations";
import { formatNumberStringToNumber } from "src/services/format";
import * as Url from "src/services/url";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { failure, success } from "src/types/remoteData";
import { FormQuestion } from "../components/Inputs/FormQuestion";
import { LateEditFormButtons } from "../components/Layout/LateEditFormButtons";
import { StudentHeader } from "../components/Layout/StudentHeader";
import {
  INSERT_FORM_ANSWER_OPTIONS,
  LATE_EDIT_SCHOOLS_RANK,
  UPSERT_FORM_ADDRESS,
  UPSERT_FORM_ANSWER_DATE,
  UPSERT_FORM_ANSWER_FREE_TEXT,
  UPSERT_FORM_ANSWER_NUMBER,
  UPSERT_GRADES_ANSWER,
} from "../graphql/mutations";
import {
  GET_FORM_ANSWERS_BY_ID,
  GET_FORM_BY_ID,
  GET_SCHOOLS_RANK,
} from "../graphql/queries";
import { LateEditRankSchools } from "./LateEditSchoolsRank";

import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  useClearAnswerForMe,
  usePutAnswerForMe,
} from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import {
  LateEditRankedSchool,
  LateEditSchool,
} from "src/scenes/parent/forms/LateEdit/types";
import { FormAddressSchema } from "src/schemas/Address";
import { triggerRuleExecutionsForSoftDeletedOffers } from "src/services/form/offer";
import { Loading } from "../components/Loading";
import { useRemoveAnswersForHiddenQuestions } from "../hooks/useRemoveAnswersForHiddenQuestions";
const TOAST_SUCCESS_ID = "late-edit-success";
const TOAST_ERROR_ID = "late-edit-error";
const TOAST_INFO_ID = "late-edit-info-success";

enum SelectTab {
  RANK_SCHOOLS = "rank-schools",
  SCHOOL_QUESTIONS = "school-questions",
  GENERAL_QUESTIONS = "general-questions",
}

function handleError(error: Error | ApolloError): React.ReactElement {
  if (error instanceof ApolloError) {
    return <ApolloErrorView error={error} />;
  }

  if (isOrganizationError(error)) {
    return <OrganizationErrorView error={error} />;
  }

  return <Alert status="error">{error.message}</Alert>;
}

type Data = {
  form: GQL.FormFragment;
  generalSections: AF.GeneralSection<AF.WithId>[];
  sections: AF.Sections<AF.WithId>;
  enrollmentPeriodName: string;
  student: AFF.Types.Applicant & Person;
  previousFormSchoolIds: uuid[];
  answers: GQL.GetFormAnswersById;
  familyEditableQuestions: readonly AF.Question<AF.WithId>[];
  schoolRankingSection?: AF.SchoolRankingSection<AF.WithId>;
};

const LateEditContent = () => {
  const flags = useFlags(["form-service-form-answers"]);
  const isFormServiceEnabled = flags["form-service-form-answers"].enabled;

  const previousVisibilityContext = useVisibilityContext();
  const formContext = useFormContext();

  const { glossary } = useGlossary();
  const navigate = useNavigate();
  const toast = useWeglotToast();
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const organization = useOrganization();
  const { id: formId = "" } = useParams();

  const [updatedSchoolRanks, setUpdatedSchoolRanks] = React.useState<
    readonly LateEditRankedSchool[]
  >([]);
  const [removedSchoolRanks, setRemovedSchoolRanks] = React.useState<
    readonly LateEditRankedSchool[]
  >([]);
  const [newSchools, setNewSchools] = React.useState<readonly LateEditSchool[]>(
    []
  );
  const [selectedTab, setSelectedTab] = React.useState<SelectTab>(
    SelectTab.RANK_SCHOOLS
  );

  const RankedSchools = useRankedSchools([]);

  const { remoteData: schoolRanksData } = useRemoteDataQuery<
    GQL.GetSchoolsRank,
    GQL.GetSchoolsRankVariables
  >(GET_SCHOOLS_RANK, {
    variables: {
      form_id: formId,
    },
    fetchPolicy: "no-cache",
  });

  const { remoteData: formData } = useRemoteDataQuery<
    GQL.GetFormById,
    GQL.GetFormByIdVariables
  >(GET_FORM_BY_ID, {
    variables: { form_id: formId },
    fetchPolicy: "no-cache",
  });

  const { remoteData: answersRemoteData } = useRemoteDataQuery<
    GQL.GetFormAnswersById,
    GQL.GetFormAnswersByIdVariables
  >(GET_FORM_ANSWERS_BY_ID, {
    fetchPolicy: "no-cache",
    variables: { form_id: formId },
  });

  const previousFormSchoolsRemoteData = usePreviousFormSchools(formId);

  const remoteData = React.useMemo(() => {
    return RD.toTuple4(
      formData,
      schoolRanksData,
      previousFormSchoolsRemoteData,
      answersRemoteData
    )
      .mapError<ApolloError | Error>((error) => error)
      .andThen(([data, schoolData, previousFormSchoolIds, answersData]) => {
        const form = data.form[0];
        if (!form?.form_template) {
          return failure<ApolloError | Error, Data>(
            new Error("Form not found")
          );
        }
        try {
          const formTemplate = FormTemplate.fromGQL(form.form_template);

          const enrollmentPeriodName =
            form.form_template.enrollment_period.name;

          const generalSections = findGeneralSections(formTemplate.sections);

          const schoolRankingSection = findSchoolRankingSection(
            formTemplate.sections
          );

          if (
            !schoolRankingSection ||
            (schoolRankingSection?.familyEditableStatuses &&
              !schoolRankingSection?.familyEditableStatuses?.includes(
                form.status
              ))
          ) {
            setSelectedTab(SelectTab.GENERAL_QUESTIONS);
          }

          setUpdatedSchoolRanks(schoolData.form_school_rank);

          if (schoolRankingSection) {
            RankedSchools.setRanks(
              schoolData.form_school_rank.map((item) => ({
                form_id: formId,
                schools_ranking_section_id: schoolRankingSection.id,
                school_id: item.school.id,
              }))
            );
          }

          const familyEditableSections = formTemplate.sections.filter(
            (section) => {
              if (!section?.familyEditableStatuses) {
                return false;
              }

              return section.familyEditableStatuses.includes(form.status);
            }
          );
          const familyEditableQuestions = familyEditableSections.flatMap(
            (section) =>
              section && "questions" in section ? section.questions : []
          );

          return success<ApolloError, Data>({
            familyEditableQuestions,
            previousFormSchoolIds,
            form,
            enrollmentPeriodName,
            schoolRankingSection,
            generalSections,
            sections: formTemplate.sections,
            answers: answersData,
            student: {
              first_name: form.person.first_name ?? "",
              last_name: form.person.last_name ?? "",
              avatar: form.person.avatar,
              birth_date: form.person.birth_date,
            },
          });
        } catch (error: unknown) {
          console.error(error);
          return failure<ApolloError | Error, Data>(error as Error);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, formData, schoolRanksData]);

  const visibilityContextWithNewSchoolsSelection: VisibilityContext =
    useMemo(() => {
      return {
        ...previousVisibilityContext,
        selectedSchoolsSet: new Set(newSchools.map((s) => s.id)),
      };
    }, [previousVisibilityContext, newSchools]);

  const specificToNewSchoolsQuestions = useMemo(() => {
    if (!remoteData.hasData() || "questions" in remoteData.data.generalSections)
      return [];

    const questions = remoteData.data.generalSections.flatMap(
      (s) => s.questions
    );

    const schoolSpecificQuestions =
      FormTemplate.Question.getSpecificToSchoolsQuestions(questions, {
        ...visibilityContextWithNewSchoolsSelection,
        selectedSchoolsSet: new Set(newSchools.map((s) => s.id)),
      });

    return schoolSpecificQuestions;
  }, [remoteData, newSchools, visibilityContextWithNewSchoolsSelection]);

  const specificToDeletedSchoolsQuestions = useMemo(() => {
    if (!remoteData.hasData() || "questions" in remoteData.data.generalSections)
      return [];

    return FormTemplate.Question.getSpecificToSchoolsQuestions(
      remoteData.data.generalSections.flatMap((s) => s.questions),
      {
        ...visibilityContextWithNewSchoolsSelection,
        selectedSchoolsSet: new Set(removedSchoolRanks.map((s) => s.school.id)),
      }
    );
  }, [
    remoteData,
    removedSchoolRanks,
    visibilityContextWithNewSchoolsSelection,
  ]);

  const familyEditableQuestions = useMemo(() => {
    if (!remoteData.hasData()) return [];

    return remoteData.data.familyEditableQuestions.filter(
      (question) =>
        !specificToNewSchoolsQuestions.some((s) => s.id === question.id) ||
        !specificToDeletedSchoolsQuestions.some((s) => s.id === question.id)
    );
  }, [
    remoteData,
    specificToNewSchoolsQuestions,
    specificToDeletedSchoolsQuestions,
  ]);

  const hasFamilyEditableQuestions = useMemo(() => {
    return familyEditableQuestions.length > 0;
  }, [familyEditableQuestions]);

  const [lateEditRankSchools] = useRemoteDataMutation<
    GQL.LateEditSchoolRanks,
    GQL.LateEditSchoolRanksVariables
  >(LATE_EDIT_SCHOOLS_RANK);

  const [upsertFreeText] = useRemoteDataMutation<
    GQL.UpsertFormAnswerFreeText,
    GQL.UpsertFormAnswerFreeTextVariables
  >(UPSERT_FORM_ANSWER_FREE_TEXT, { fetchPolicy: "no-cache" });

  const [upsertNumber] = useRemoteDataMutation<
    GQL.UpsertFormAnswerNumber,
    GQL.UpsertFormAnswerNumberVariables
  >(UPSERT_FORM_ANSWER_NUMBER, { fetchPolicy: "no-cache" });

  const [upsertDate] = useRemoteDataMutation<
    GQL.UpsertFormAnswerDate,
    GQL.UpsertFormAnswerDateVariables
  >(UPSERT_FORM_ANSWER_DATE, { fetchPolicy: "no-cache" });

  const [insertAnswerOptions] = useRemoteDataMutation<
    GQL.InsertFormAnswerOptions,
    GQL.InsertFormAnswerOptionsVariables
  >(INSERT_FORM_ANSWER_OPTIONS, { fetchPolicy: "no-cache" });

  const [upsertGrades] = useRemoteDataMutation<
    GQL.UpsertGradesAnswer,
    GQL.UpsertGradesAnswerVariables
  >(UPSERT_GRADES_ANSWER, { fetchPolicy: "no-cache" });

  const [upsertFormAddress] = useRemoteDataMutation<
    GQL.UpsertFormAddress,
    GQL.UpsertFormAddressVariables
  >(UPSERT_FORM_ADDRESS, { fetchPolicy: "no-cache" });

  const { mutateAsync: clearAnswerForMe } = useClearAnswerForMe(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const { mutateAsync: putAnswerForMe } = usePutAnswerForMe(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const saveAnswers = useCallback(
    async (
      answers: FormTemplate.Answer.FormikValues,
      narrowDownQuestions: readonly AF.Question<AF.WithId>[],
      formId: string
    ) => {
      for (const questionId of Object.keys(answers)) {
        const question = narrowDownQuestions?.find(
          (question) => question.id === questionId
        );

        if (question?.type === GQL.question_type_enum.SingleSelect) {
          if (!isSingleSelectAnswer(answers[questionId])) {
            console.error(
              `Expecting a "string" for question ${question.id}, but got ${answers[questionId]} instead`
            );
            continue;
          }

          if (isFormServiceEnabled) {
            if (!answers[questionId]) {
              await clearAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
              });
            } else {
              await putAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
                body: {
                  data: answers[questionId],
                },
              });
            }
          } else {
            await insertAnswerOptions({
              variables: {
                form_id: formId,
                question_id: question.id,
                options: answers[questionId]
                  ? [
                      {
                        form_question_option_id:
                          answers[questionId]?.toString(),
                      },
                    ]
                  : [],
              },
            });
          }
        } else if (question?.type === GQL.question_type_enum.MultiSelect) {
          if (!isMultiSelectAnswer(answers[questionId])) {
            console.error(
              `Expecting a "string[]" for question ${question.id}, but got ${answers[questionId]} instead`
            );
            continue;
          }

          const answersOptions = answers[questionId] as string[];

          if (isFormServiceEnabled) {
            if (!answersOptions.length) {
              await clearAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
              });
            } else {
              await putAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
                body: {
                  data: answersOptions,
                },
              });
            }
          } else {
            await insertAnswerOptions({
              variables: {
                form_id: formId,
                question_id: question.id,
                options: answersOptions.length
                  ? answersOptions.map((value) => ({
                      form_question_option_id: value,
                    }))
                  : [],
              },
            });
          }
        } else if (
          question?.type === GQL.question_type_enum.FreeText ||
          question?.type === GQL.question_type_enum.PhoneNumber ||
          question?.type === GQL.question_type_enum.Email
        ) {
          if (!isFreeTextAnswer(answers[questionId])) {
            console.error(
              `Expecting a "string" for question ${question.id}, but got ${answers[questionId]} instead`
            );
            continue;
          }
          if (isFormServiceEnabled) {
            if (!answers[questionId]) {
              await clearAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
              });
            } else {
              await putAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
                body: {
                  data: answers[questionId],
                },
              });
            }
          } else {
            await upsertFreeText({
              variables: {
                form_id: formId,
                question_id: question.id,
                free_text_answer: answers[questionId]?.toString(),
              },
            });
          }
        } else if (question?.type === GQL.question_type_enum.Number) {
          const number = formatNumberStringToNumber(answers[questionId]);
          if (isFormServiceEnabled) {
            if (!number) {
              await clearAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
              });
            } else {
              await putAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
                body: {
                  data: number,
                },
              });
            }
          } else {
            await upsertNumber({
              variables: {
                form_id: formId,
                question_id: question.id,
                number_answer: number,
              },
            });
          }
        } else if (question?.type === GQL.question_type_enum.Date) {
          const stringDate = answers[questionId]?.toString();

          if (!isFreeTextAnswer(stringDate)) {
            console.error(
              `Expecting a "string" for question ${question.id}, but got ${stringDate} instead`
            );
            return false;
          }

          if (isFormServiceEnabled) {
            if (!stringDate) {
              await clearAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
              });
            } else {
              await putAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
                body: {
                  data: stringDate,
                },
              });
            }
          } else {
            await upsertDate({
              variables: {
                form_id: formId,
                question_id: question.id,
                date_answer:
                  stringDate && stringDate.length > 0 ? stringDate : null,
              },
            });
          }
          return true;
        } else if (question?.type === GQL.question_type_enum.Grades) {
          if (!isGradesAnswer(answers[questionId])) {
            console.error(
              `Expecting a "string[]" for question ${question.id}, but got ${answers[questionId]} instead`
            );
            continue;
          }
          if (isFormServiceEnabled) {
            if (!answers[questionId]) {
              await clearAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
              });
            } else {
              await putAnswerForMe({
                path: {
                  formId: formId,
                  questionId: question.id,
                },
                body: {
                  data: answers[questionId]?.toString(),
                },
              });
            }
          } else {
            await upsertGrades({
              variables: {
                form_id: formId,
                question_id: question.id,
                grade_config_id: answers[questionId]?.toString(),
              },
            });
          }
        } else if (question?.type === GQL.question_type_enum.Address) {
          const address = FormAddressSchema.parse(answers[questionId]);
          if (isFormServiceEnabled) {
            await putAnswerForMe({
              path: {
                formId: formId,
                questionId: question.id,
              },
              body: {
                data: address,
              },
            });
          } else {
            await upsertFormAddress({
              variables: {
                address: {
                  form_id: formId,
                  question_id: question.id,
                  ...address,
                },
              },
            });
          }
        }
      }
    },
    [
      clearAnswerForMe,
      insertAnswerOptions,
      isFormServiceEnabled,
      putAnswerForMe,
      upsertDate,
      upsertFreeText,
      upsertGrades,
      upsertNumber,
      upsertFormAddress,
    ]
  );

  const handleDeletedSchoolRank = (schoolRank: LateEditRankedSchool) => {
    if (schoolRank.offers[0]?.status === GQL.offer_status_enum.Accepted) return;
    setRemovedSchoolRanks([...removedSchoolRanks, schoolRank]);
    setUpdatedSchoolRanks(
      updatedSchoolRanks
        .filter((s) => s.school.id !== schoolRank.school.id)
        .map((s, index) => {
          return {
            ...s,
            rank: index,
          };
        })
    );
    setNewSchools(newSchools.filter((s) => s.id !== schoolRank.school.id));

    toast({
      id: TOAST_INFO_ID,
      status: "info",
      title: glossary`School removed from list.`,
      isClosable: true,
    });
  };

  const handleLateSelectSchool = (school: School) => {
    const newSchool: LateEditRankedSchool = {
      school,
      rank: updatedSchoolRanks.length + newSchools.length,
      sub_status: null,
      waitlists: [],
      offers: [],
    };
    setNewSchools([...newSchools, school]);
    setUpdatedSchoolRanks([...updatedSchoolRanks, newSchool]);
  };

  const removeAnswersForHiddenQuestions = useRemoveAnswersForHiddenQuestions({
    formId,
  });
  const handleUpdateGeneralQuestions = useCallback(
    async (
      answers: FormTemplate.Answer.FormikValues,
      generalQuestions: readonly AF.Question<AF.WithId>[]
    ) => {
      if (!remoteData.hasData()) return;

      try {
        if (answers && generalQuestions) {
          await saveAnswers(answers, generalQuestions, remoteData.data.form.id);
          await removeAnswersForHiddenQuestions();

          try {
            await FSR.createRuleExecutionsForMe({
              body: {
                data: {
                  targets: [{ type: "form", formId: remoteData.data.form.id }],
                },
              },
            });
          } catch (error) {
            console.warn("Error executing form rules:", error);
          }
        }
        navigate(organization.map(Url.Parent.index).withDefault("#"));
      } catch (err) {
        console.error(err);
      }
    },
    [
      navigate,
      organization,
      remoteData,
      removeAnswersForHiddenQuestions,
      saveAnswers,
    ]
  );

  const handleUpdateSchoolRanks = useCallback(
    async (
      answers?: FormTemplate.Answer.FormikValues,
      specificToNewSchoolsQuestions?: readonly AF.Question<AF.WithId>[]
    ) => {
      if (!remoteData.hasData()) return;
      const schoolRankingSectionId = remoteData.data.schoolRankingSection?.id;
      if (!schoolRankingSectionId) return;

      try {
        if (newSchools.length && answers && specificToNewSchoolsQuestions) {
          await saveAnswers(
            answers,
            specificToNewSchoolsQuestions,
            remoteData.data.form.id
          );
        }

        const schoolRanksData = updatedSchoolRanks.map((schoolRank) => ({
          form_id: remoteData.data.form.id,
          schools_ranking_section_id: schoolRankingSectionId,
          school_id: schoolRank.school.id,
          rank: schoolRank.rank,
        }));

        const upsertedSchoolRanks =
          RankedSchools.getUpsertedRanks(schoolRanksData);
        const deletedSchoolRanks =
          RankedSchools.getDeletedRanks(upsertedSchoolRanks);
        const deletedOffers =
          RankedSchools.getDeletedOffers(upsertedSchoolRanks);
        const deletedWaitlists =
          RankedSchools.getDeletedWaitlists(upsertedSchoolRanks);

        if (flags["form-service-form-answers"].enabled) {
          await putAnswerForMe({
            path: {
              formId: remoteData.data.form.id,
              questionId: schoolRankingSectionId,
            },
            body: {
              data: upsertedSchoolRanks.map((rank, index) => ({
                schoolId: rank.school_id ?? "",
                rank: rank.rank ?? index,
              })),
            },
          });
        } else {
          const result = await lateEditRankSchools({
            variables: {
              deleted_school_ranks: deletedSchoolRanks,
              upserted_school_ranks: upsertedSchoolRanks,
              delete_offers_where: deletedOffers,
              delete_waitlists_where: deletedWaitlists,
            },
          });
          await triggerRuleExecutionsForSoftDeletedOffers({
            offers: result.data?.update_offer?.returning ?? [],
            scope: "me",
          });
        }

        try {
          await FSR.createRuleExecutionsForMe({
            body: {
              data: {
                targets: [{ type: "form", formId: remoteData.data.form.id }],
              },
            },
          });
        } catch (error) {
          console.warn("Error executing form rules:", error);
        }

        await refreshFormAdmissionsAfterMutation(
          queryClient,
          remoteData.data.form.id
        );
        await apolloClient.refetchQueries({
          include: [GET_PARENT_FORMS_INDEX, GET_PARENT_ASSIGNED_FORMS],
        });

        if (!toast.isActive(TOAST_SUCCESS_ID)) {
          toast({
            id: TOAST_ERROR_ID,
            status: "success",
            title: glossary`Schools updated`,
            isClosable: true,
          });
        }

        if (hasFamilyEditableQuestions) {
          setSelectedTab(SelectTab.GENERAL_QUESTIONS);
        } else {
          navigate(organization.map(Url.Parent.index).withDefault("#"));
        }
      } catch (err) {
        if (!toast.isActive(TOAST_ERROR_ID)) {
          toast({
            id: TOAST_ERROR_ID,
            status: "error",
            title: glossary`Error while updating schools`,
            description:
              "Please try again later or report the problem to our support team.",
            isClosable: true,
          });
        }
      }
    },
    [
      remoteData,
      newSchools,
      updatedSchoolRanks,
      RankedSchools,
      flags,
      toast,
      hasFamilyEditableQuestions,
      saveAnswers,
      putAnswerForMe,
      lateEditRankSchools,
      glossary,
      navigate,
      organization,
      queryClient,
      apolloClient,
    ]
  );

  const verifyNextStep = useCallback(
    (generalQuestions: readonly AF.Question<AF.WithId>[]) => {
      if (!newSchools.length) {
        return handleUpdateSchoolRanks();
      }

      const validQuestions =
        FormTemplate.Question.getSpecificToSchoolsQuestions(generalQuestions, {
          ...visibilityContextWithNewSchoolsSelection,
          selectedSchoolsSet: new Set(newSchools.map((s) => s.id)),
        });

      if (!validQuestions.length) {
        return handleUpdateSchoolRanks();
      }

      setSelectedTab(SelectTab.SCHOOL_QUESTIONS);
    },
    [
      newSchools,
      visibilityContextWithNewSchoolsSelection,
      handleUpdateSchoolRanks,
    ]
  );

  const getInitialValues = (
    generalQuestions: AF.Question<AF.WithId>[],
    answers: GQL.GetFormAnswersById
  ) =>
    FormTemplate.Answer.getFormikInitialValues(
      generalQuestions,
      answers.form_answer,
      answers.grades_answer,
      answers.form_address,
      answers.custom_question_answer
    );

  const leftNavigationOverride = useMemo(() => {
    switch (selectedTab) {
      case SelectTab.RANK_SCHOOLS:
        return {
          label: "Cancel",
          action: () =>
            navigate(organization.map(Url.Parent.index).withDefault("#")),
        };
      case SelectTab.SCHOOL_QUESTIONS:
        return {
          label: "Back",
          action: () => setSelectedTab(SelectTab.RANK_SCHOOLS),
        };
      case SelectTab.GENERAL_QUESTIONS:
        return {
          label: "Cancel",
          action: () =>
            navigate(organization.map(Url.Parent.index).withDefault("#")),
        };
    }
  }, [selectedTab, navigate, organization]);

  // Since we don't use formik form submission modle for this form, we need to keep track the isSubmitting manually
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleRankSchoolsNavigation = useCallback(
    (generalQuestions: readonly AF.Question<AF.WithId>[]) => ({
      label: "Next",
      isLoading: isSubmitting,
      action: async () => {
        setSubmitting(true);
        await verifyNextStep(generalQuestions);
        await Promise.all([formContext.refetchSelectedSchools()]);
        setSubmitting(false);
      },
    }),
    [formContext, isSubmitting, verifyNextStep]
  );

  const handleSchoolQuestionsNavigation = useCallback(
    (
      formikProps: FormikProps<FormTemplate.Answer.FormikValues>,
      schoolQuestions: readonly AF.Question<AF.WithId>[]
    ) => ({
      label: glossary`Update schools`,
      isLoading: isSubmitting,
      action: async () => {
        if (formikProps.isValid) {
          setSubmitting(true);
          await handleUpdateSchoolRanks(formikProps.values, schoolQuestions);
          await Promise.all([
            formContext.refetchSelectedSchools(),
            formContext.refetchAnswers(),
          ]);
        }
        setSubmitting(false);
      },
      hide: !formikProps.isValid,
    }),
    [formContext, glossary, handleUpdateSchoolRanks, isSubmitting]
  );

  const handleGeneralQuestionsNavigation = useCallback(
    (
      formikProps: FormikProps<FormTemplate.Answer.FormikValues>,
      generalQuestions: readonly AF.Question<AF.WithId>[]
    ) => ({
      label: "Update",
      isLoading: isSubmitting,
      action: async () => {
        if (formikProps.isValid) {
          setSubmitting(true);
          await handleUpdateGeneralQuestions(
            formikProps.values,
            generalQuestions
          );
        }
        setSubmitting(false);
      },
      hide: !formikProps.isValid,
    }),
    [handleUpdateGeneralQuestions, isSubmitting]
  );

  const rightNavigationOverride = useCallback(
    (
      generalQuestions: readonly AF.Question<AF.WithId>[],
      formikProps: FormikProps<FormTemplate.Answer.FormikValues>,
      schoolQuestions: readonly AF.Question<AF.WithId>[],
      familyEditableQuestions: readonly AF.Question<AF.WithId>[]
    ) => {
      switch (selectedTab) {
        case SelectTab.RANK_SCHOOLS:
          return handleRankSchoolsNavigation(generalQuestions);
        case SelectTab.SCHOOL_QUESTIONS:
          return handleSchoolQuestionsNavigation(formikProps, schoolQuestions);
        case SelectTab.GENERAL_QUESTIONS:
          return handleGeneralQuestionsNavigation(
            formikProps,
            familyEditableQuestions
          );
      }
    },
    [
      selectedTab,
      handleRankSchoolsNavigation,
      handleSchoolQuestionsNavigation,
      handleGeneralQuestionsNavigation,
    ]
  );

  return (
    <RemoteDataView
      error={handleError}
      remoteData={remoteData}
      loading={<Loading />}
    >
      {({
        form,
        schoolRankingSection,
        generalSections,
        sections,
        enrollmentPeriodName,
        student,
        previousFormSchoolIds,
        answers,
      }) => {
        if (!LATE_EDIT_STATUSES.includes(form.status))
          return <GenericError message="This form it's not locked." />;

        const generalQuestions = generalSections.flatMap(
          (section) => section.questions
        );

        return (
          <Formik
            initialValues={getInitialValues(
              familyEditableQuestions.concat(specificToNewSchoolsQuestions),
              answers
            )}
            onSubmit={() => {}}
            enableReinitialize
            validate={
              selectedTab === SelectTab.SCHOOL_QUESTIONS
                ? validateFormTemplate(
                    specificToNewSchoolsQuestions,
                    {
                      rankedSchoolIds: newSchools.map((s) => s.id),
                      previousFormSchoolIds,
                    },
                    visibilityContextWithNewSchoolsSelection
                  )
                : selectedTab === SelectTab.GENERAL_QUESTIONS
                ? validateFormTemplate(
                    familyEditableQuestions,
                    {
                      rankedSchoolIds: updatedSchoolRanks.map(
                        (s) => s.school.id
                      ),
                      previousFormSchoolIds,
                    },
                    visibilityContextWithNewSchoolsSelection
                  )
                : undefined
            }
          >
            {(formikProps) => {
              const schoolQuestions = getCompleteApplicableQuestions(
                specificToNewSchoolsQuestions,
                formikProps.values,
                {
                  rankedSchoolIds: newSchools.map((s) => s.id),
                  previousFormSchoolIds,
                },
                visibilityContextWithNewSchoolsSelection
              );
              const localFamilyEditableQuestions =
                getCompleteApplicableQuestions(
                  familyEditableQuestions,
                  formikProps.values,
                  {
                    rankedSchoolIds: updatedSchoolRanks.map((s) => s.school.id),
                    previousFormSchoolIds,
                  },
                  visibilityContextWithNewSchoolsSelection
                );

              return (
                <FormLayout
                  as={FormWithAnswersTracker}
                  heading={glossary`Edit schools`}
                  buttons={
                    <LateEditFormButtons
                      overridePreviousButton={leftNavigationOverride}
                      overrideNextButton={rightNavigationOverride(
                        generalQuestions,
                        formikProps,
                        schoolQuestions,
                        localFamilyEditableQuestions
                      )}
                    />
                  }
                  backLink={{
                    url: organization.map(Url.Parent.index).withDefault("#"),
                    label: "Back to forms",
                  }}
                >
                  <StudentHeader
                    student={student}
                    enrollmentPeriodName={enrollmentPeriodName}
                  />
                  {selectedTab === SelectTab.RANK_SCHOOLS &&
                    schoolRankingSection && (
                      <LateEditRankSchools
                        onLateSchoolsUpdate={setUpdatedSchoolRanks}
                        formTemplateId={form.form_template.id}
                        formId={formId}
                        enrollmentPeriodId={
                          form.form_template.enrollment_period_id
                        }
                        section={schoolRankingSection}
                        allSections={sections}
                        newSchools={newSchools}
                        schoolRanks={updatedSchoolRanks}
                        onLateDeleteSchoolRank={handleDeletedSchoolRank}
                        onLateSelectSchool={handleLateSelectSchool}
                      />
                    )}
                  {selectedTab === SelectTab.SCHOOL_QUESTIONS &&
                    schoolQuestions.map((question) => (
                      <FormQuestion
                        formId={form.id}
                        applicant={student}
                        key={question.id}
                        question={question}
                        marginBottom={4}
                      />
                    ))}
                  {selectedTab === SelectTab.GENERAL_QUESTIONS &&
                    localFamilyEditableQuestions.map((question) => (
                      <FormQuestion
                        formId={form.id}
                        applicant={student}
                        key={question.id}
                        question={question}
                        marginBottom={4}
                      />
                    ))}
                </FormLayout>
              );
            }}
          </Formik>
        );
      }}
    </RemoteDataView>
  );
};

export const LateEdit = () => {
  const { id: formId = "" } = useParams();
  return (
    <FormProvider formId={formId}>
      {(formTemplateContextRD, formContextRD) => {
        return (
          <RemoteDataView
            error={handleError}
            remoteData={RD.toTuple(formTemplateContextRD, formContextRD)}
            loading={<Loading />}
          >
            {() => <LateEditContent />}
          </RemoteDataView>
        );
      }}
    </FormProvider>
  );
};
